import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
    SettingOption,
    Define,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import TableField from '@appblocks/codegeneration/SettingsConfigurations/TableField';
import AzureTelemetry from '@appblocks/codegeneration/Libraries/AzureTelemetry';
import AzureProperty from '@appblocks/codegeneration/Libraries/AzureProperty';
import AzureCommand from '@appblocks/codegeneration/Libraries/AzureCommand';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';

export default class Azure extends BaseFeature {
    constructor() {
        super();
        this.name = 'Azure IoT Central';
        this.id = 'azure';
        this.icon = 'VscAzure';
        this.fileLinks = [
            // 'mqtt\\mqtt.tbs',
            // 'mqtt\\mqtt.tbh',
        ];
        this.files = [
            'libraries/mqtt/mqtt.tbh',
            'libraries/mqtt/mqtt.tbs',
            'libraries/azure/azure.tbh',
            'libraries/azure/azure.tbs',
            'azure.cer',
        ];
        this.requirements = [['TPP2W(G3)', 'TPP3W(G3)', 'WM2000EV', 'WM2000', 'WS1102']];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Azure Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'AZURE_INTERFACE',
                name: 'Interface',
                settingType: SettingType.OPTION,
                required: true,
                default: 'PL_SOCK_INTERFACE_NET',
                options: [],
            },
            {
                id: 'AZURE_DEBUG_PRINT',
                name: 'Debug Print',
                settingType: SettingType.OPTION,
                required: true,
                helpText: 'Debug print messages',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'AZURE_DEVICE_NAME',
                name: 'Device Name',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'ANAME',
                    NAME: 'Azure Device',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: 'Device_Name_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: 'Device_Name_1',
            },
            {
                id: 'AZURE_DEVICE_ID',
                name: 'Device ID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'ADID',
                    NAME: 'Azure Device ID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: 'Device_ID_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: 'Device_ID_1',
            },
            {
                id: 'AZURE_SCOPE_ID',
                name: 'ID Scope',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'ASID',
                    NAME: 'Azure Scope ID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: 'Scope_ID_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: 'Scope_ID_1',
            },
            {
                id: 'AZURE_DPS_SERVER',
                name: 'DPS Server',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'ADPSS',
                    NAME: 'Azure DPS Server',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: 'global.azure-devices-provisioning.net',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: 'global.azure-devices-provisioning.net',
            },
            {
                id: 'AZURE_PRIMARY_KEY',
                name: 'Primary Key',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'APKEY',
                    NAME: 'Azure Primary Key',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: 'Primary_Key_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: 'Primary_Key_1',
            },
            {
                id: 'TELEMETRY',
                name: 'Telemetry',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'azure_telemetry',
                default: [
                    new AzureTelemetry(),
                ],
            },
            {
                id: 'PROPERTY',
                name: 'Property',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'azure_property',
                default: [
                    new AzureProperty(),
                ],
            },
            {
                id: 'COMMAND',
                name: 'Command',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'azure_command',
                default: [
                    new AzureCommand(),
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getFiles() {
        const fileArray = [];
        fileArray.push('azure.cer');
        return this.files.concat(fileArray);
    }

    getDependencies() {
        const dependencies = [
            'mqtt_lib',
            'stg',
            'http',
            'tbl',
        ];
        return dependencies;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const { project } = this;
        if (settingId === 'AZURE_INTERFACE') {
            return project.getNetworkInterfaces();
        }
        return super.getSettingOptions(settingId);
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.HIDDEN;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getDefines(): Array<Define> {
        const defines = [
            {
                name: 'MQTT_TLS_BUFF_PAGES',
                value: '150',
            },
            {
                name: 'HTTP_TLS_BUFF_PAGES',
                value: '150',
            },
        ];
        const AZURE_DEBUG_PRINT = this.getSettingValue('AZURE_DEBUG_PRINT');
        if (AZURE_DEBUG_PRINT === '1') {
            defines.push({
                name: 'MQTT_DEBUG_PRINT',
                value: '1',
            });
            defines.push({
                name: 'HTTP_DEBUG_PRINT',
                value: '1',
            });
        }
        return defines;
    }

    getCodeBlocks() {
        let blocks: Array<CodeBlock> = [];
        if (this.getSettingValue('ENABLED').toString() === '0') {
            return blocks;
        }

        const TELEMETRY_ARRAY = this.getSettingValue('TELEMETRY');
        let settingsFile = '';
        settingsFile += `
#define AZURE_DEBUG_PRINT 1
#define AZURE_ITEM_NAME_LEN 16
#define AZURE_ITEM_VALUE_LEN 32
#define AZURE_TELEMETRY_NUM 2
#define AZURE_PROPERTY_NUM 0
#define AZURE_COMMAND_NUM 0`;

        const COMMAND_ARRAY = this.getSettingValue('COMMAND');
        let command_template = 'select case command';
        for (let i = 0; i < COMMAND_ARRAY.length; i++) {
            const COMMAND_NAME = COMMAND_ARRAY[i].getSettingValue('COMMAND_ID');
            command_template += `
    case "${COMMAND_NAME}":
    '****callback_azure_command_triggered_${COMMAND_NAME}****'`;
        }

        command_template += `
    case else:
    end select`;

        blocks.push({
            location: 'callback_azure_command_triggered',
            blockType: BlockType.CODE,
            template: command_template,
        });

        blocks.push({
            location: 'callback_http_open_romfile',
            blockType: BlockType.CODE,
            template: `
    if http_request_item.url = azure_current_url then
        'romfile.open("azure.cer")
        'callback_http_open_romfile=romfile.offset
        callback_http_open_romfile=0
    end if
    `,
        });

        for (let i = 0; i < TELEMETRY_ARRAY.length; i += 1) {
            const TELEMETRY = TELEMETRY_ARRAY[i];
            blocks = blocks.concat(TELEMETRY.getCodeBlocks());
        }

        blocks.push({
            location: 'callback_http_header_arrival',
            blockType: BlockType.CODE,
            template: `
    iot_header_arrival(header_type, header_value)
    `,
        });

        blocks.push({
            location: 'callback_http_send_headers',
            blockType: BlockType.CODE,
            template: `
    iot_send_headers()
    `,
        });

        blocks.push({
            location: 'callback_http_content_arrival',
            blockType: BlockType.CODE,
            template: `
    azure_content_arrival(data)
`,
        });

        blocks.push({
            location: 'DEVICE_TBS_DIMS',
            blockType: BlockType.CODE,
            template: `
dim azure_current_url as string
`,
        });

        blocks.push({
            location: 'callback_http_request_complete',
            blockType: BlockType.CODE,
            template: `
    azure_http_request_complete()
`,
        });

        // not supported
        //         blocks.push({
        //             location: 'GLOBAL_DEFINES',
        //             blockType: BlockType.CODE,
        //             template: `
        // #define MQTT_CER_FILE "azure.cer"`,
        //         });

        blocks.push({
            location: this.project.getNetworkConnectedLocation(this.getSettingValue('AZURE_INTERFACE')),
            blockType: BlockType.CODE,
            template: 'azure_start(AZURE_DEVICE_ID,AZURE_SCOPE_ID,AZURE_DPS_SERVER,AZURE_PRIMARY_KEY,AZURE_INTERFACE_SELECTED)',
        });

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const AZURE_DEVICE_ID=${this.getSettingValue('AZURE_DEVICE_ID')}
const AZURE_SCOPE_ID=${this.getSettingValue('AZURE_SCOPE_ID')}
const AZURE_DPS_SERVER=${this.getSettingValue('AZURE_DPS_SERVER')}
const AZURE_PRIMARY_KEY=${this.getSettingValue('AZURE_PRIMARY_KEY')}
const AZURE_INTERFACE_SELECTED=${this.getSettingValue('AZURE_INTERFACE')}
`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim azure_device_name as string = ${this.getSettingValue('AZURE_DEVICE_NAME')}
`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare azure_device_name as string`,
        });

        blocks.push({
            location: 'on_sys_timer',
            blockType: BlockType.CODE,
            template: `
    azure_proc_timer()`,
        });

        blocks.push({
            location: 'on_sock_data_sent',
            blockType: BlockType.CODE,
            template: `
    azure_on_telemetry_sent()`,
        });

        blocks.push({
            location: 'callback_mqtt_connect_lost',
            blockType: BlockType.CODE,
            template: `
    azure_on_disconnected()`,
        });

        blocks.push({
            location: 'callback_mqtt_notif',
            blockType: BlockType.CODE,
            template: `
    azure_proc_mqtt_msg(topic,data)
            `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            id: 'callback_azure_command_triggered',
            template: `
sub callback_azure_command_triggered(byref command as string, byref param as string, rid as string)
    '****callback_azure_command_triggered****'
    azure_command_respond(command,"{}",rid,EN_AZURE_OK)
end sub
        `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            id: 'callback_azure_dps_failed',
            template: `
sub callback_azure_dps_failed()
    '****callback_azure_dps_failed****'
end sub
`,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            id: 'callback_azure_device_assigned',
            template: `
sub callback_azure_device_assigned(byref assigned_hub as string)
    '****callback_azure_device_assigned****'
end sub
        `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            id: 'callback_on_telemetry_sent',
            template: `
sub callback_on_telemetry_sent()
    '****callback_on_telemetry_sent****'
    dim active_rec_num as word
    tbl_select("Azure","Azure")
    tbl_get_num_records(active_rec_num,NO)
    if active_rec_num>0 then
        tbl_record_delete(1)
    end if
end sub        
        `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            id: 'callback_azure_property_desired',
            template: `
sub callback_azure_property_desired(byref property_name as string, byref property_value as string)
    select case property_name
    '****callback_azure_property_desired****'
    case else:

    end select
end sub
        `,
        });

        const azureProperties = this.getSettingValue('PROPERTY');
        for (let i = 0; i < azureProperties.length; i++) {
            const azureProperty = azureProperties[i];
            blocks = blocks.concat(azureProperty.getCodeBlocks());
        }

        blocks.push({
            location: 'callback_mqtt_connect_ok',
            blockType: BlockType.CODE,
            template: `
    azure_on_connected()
`,
        });


        return blocks;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const project: ProjectConfiguration = this.project;

        // project.addNewNode('node_system', 'boot', 'node_azure', 'azure_connect');
        const TBL = project.getFeature('tbl') as BaseNode;
        const tablesArray = TBL.getSettingValue('TABLES');
        let AzureTableAdded = false;
        for (let i = 0; i < tablesArray.length; i++) {
            if (tablesArray[i].getSettingValue('NAME') === 'Azure') {
                AzureTableAdded = true;
            }
        }

        if (!AzureTableAdded) {
            const azureTableFields = [];

            const timeField = new TableField();
            timeField.initSettings(project);
            timeField.setSettingValue('NAME', 'time');
            timeField.setSettingValue('FIELDTYPE', 'S');
            timeField.setSettingValue('P1', '20');
            timeField.setSettingValue('P2', '24');
            timeField.setSettingValue('FORMAT', '0');
            timeField.setSettingValue('DEFAULT', '2000-00-00T00:00:00Z');
            timeField.setSettingValue('COMMENT', 'Timestamp');
            azureTableFields.push(timeField);

            const typeField = new TableField();
            typeField.initSettings(project);
            typeField.setSettingValue('NAME', 'type');
            typeField.setSettingValue('FIELDTYPE', 'S');
            typeField.setSettingValue('P1', '0');
            typeField.setSettingValue('P2', '24');
            typeField.setSettingValue('FORMAT', '0');
            typeField.setSettingValue('DEFAULT', '^');
            typeField.setSettingValue('COMMENT', 'Telemetry Type');
            azureTableFields.push(typeField);

            const valueField = new TableField();
            valueField.initSettings(project);
            valueField.setSettingValue('NAME', 'value');
            valueField.setSettingValue('FIELDTYPE', 'S');
            valueField.setSettingValue('P1', '0');
            valueField.setSettingValue('P2', '24');
            valueField.setSettingValue('FORMAT', '0');
            valueField.setSettingValue('DEFAULT', '^');
            valueField.setSettingValue('COMMENT', 'Telemetry Value');
            azureTableFields.push(valueField);

            const azureTable = new TableItem();
            azureTable.initSettings(project);
            azureTable.setSettingValue('NAME', 'Azure');
            azureTable.setSettingValue('MAXRECORDS', '1000');
            azureTable.setSettingValue('STRUCT', 'L');
            azureTable.setSettingValue('KEYFIELDS', '0');

            azureTable.setSettingValue('FIELDS', azureTableFields);

            errors.push({
                messageType: ValidationMessageType.ADD,
                message: '',
                featureId: TBL.getID(),
                settingId: 'TABLES',
                data: FeatureUtils.toJson(azureTable),
            });
        }

        return errors;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/phm/wm2000ev_demo_03';
    }
}
